import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BellIcon, CheckIcon, XIcon, Loader2Icon, ShieldCheckIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  useNotifications,
  useUnreadCount,
  useMarkAsRead,
  useMarkAllAsRead,
  useDeleteNotification,
  Notification,
} from '@/features/notifications/hooks/useNotifications';
import { formatDistanceToNow } from 'date-fns';
import { useRTL } from '@/shared/hooks/useRTL';
import { BellNotificationBubble } from './BellNotificationBubble';

/**
 * NotificationBell Component
 * Bell icon with badge showing unread count and dropdown with notifications list
 */
export function NotificationBell() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const isRTL = useRTL();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [isWiggling, setIsWiggling] = useState(false);
  const [showBubble, setShowBubble] = useState(false);
  const [bubbleMessage, setBubbleMessage] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fetch unread count for bell badge
  const { data: unreadData } = useUnreadCount();
  const unreadCount = unreadData?.data?.count || 0;

  // Fetch notifications list (only when dropdown is open)
  const { data: notificationsData, isLoading } = useNotifications({
    page: 1,
    limit: 10,
  });
  const notifications = notificationsData?.data || [];

  // Mutations
  const markAsRead = useMarkAsRead();
  const markAllAsRead = useMarkAllAsRead();
  const deleteNotification = useDeleteNotification();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Listen for booking success events and trigger wiggle animation + bubble
  useEffect(() => {
    const handleBookingSuccess = (event: CustomEvent) => {
      // Trigger wiggle animation
      setIsWiggling(true);
      setTimeout(() => setIsWiggling(false), 2000);

      // Show bubble notification
      const message = event.detail?.message || '🎮 BOOKING SUBMITTED! Awaiting approval...';
      setBubbleMessage(message);
      setShowBubble(true);

      // Refetch notifications immediately
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    };

    window.addEventListener('booking-success', handleBookingSuccess as EventListener);

    return () => {
      window.removeEventListener('booking-success', handleBookingSuccess as EventListener);
    };
  }, [queryClient]);

  // Check if notification is for admins
  const isAdminNotification = (notification: Notification) => {
    return notification.type === 'PENDING_APPROVAL';
  };

  // Handle notification click
  const handleNotificationClick = (notification: Notification) => {
    // Mark as read
    if (!notification.isRead) {
      markAsRead.mutate(notification.id);
    }

    // Navigate based on notification type
    if (notification.bookingId) {
      // For booking notifications, navigate to Weekly Calendar booking modal or admin approvals
      if (notification.link && (notification.link.includes('/admin/approvals') || notification.link.includes('/admin/pending-approvals'))) {
        // For admin pending approvals, navigate to specific booking detail (support both old and new paths)
        navigate(`/admin/approvals/${notification.bookingId}`);
      } else {
        // Navigate directly to booking modal URL (defaults to rooms calendar, booking will be found regardless)
        navigate(`/calendar/rooms/booking/${notification.bookingId}`);
      }
    } else if (notification.link) {
      // For non-booking notifications, use the link
      navigate(notification.link);
    }

    setIsOpen(false);
  };

  // Handle mark all as read
  const handleMarkAllAsRead = (e: React.MouseEvent) => {
    e.stopPropagation();
    markAllAsRead.mutate();
  };

  // Handle delete notification
  const handleDelete = (e: React.MouseEvent, notificationId: string) => {
    e.stopPropagation();
    deleteNotification.mutate(notificationId);
  };

  return (
    <>
      <div className="relative" ref={dropdownRef}>
        {/* Bell Icon Button - Neobrutalism Style */}
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="neo-icon-btn relative flex items-center justify-center rounded-full"
          style={{
            width: '48px',
            height: '48px',
            backgroundColor: 'var(--theme-surface)',
            color: 'var(--theme-text)',
          }}
          aria-label={t('notifications.bellButton')}
        >
          <BellIcon className="h-6 w-6" />
        </button>

        {/* Unread Badge */}
        {unreadCount > 0 && (
          <span
            className="absolute flex items-center justify-center text-[7px] font-bold"
            style={{
              top: '-4px',
              right: '-4px',
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              backgroundColor: '#FFB6F4',
              color: '#000000',
              border: '2px solid var(--theme-border)',
              boxShadow: '2px 2px 0px var(--theme-border)',
            }}
          >
            {unreadCount > 99 ? '99' : unreadCount}
          </span>
        )}

        {/* Dropdown Menu */}
        {isOpen && (
        <div
          dir={isRTL ? 'rtl' : 'ltr'}
          className={`neo-dropdown-menu absolute ${isRTL ? 'left-0' : 'right-0'} mt-2 w-96 max-w-[90vw] z-50`}
          style={{
            backgroundColor: 'var(--theme-card-yellow)',
          }}
        >
          {/* Header */}
          <div
            className="flex items-center justify-between p-4"
            style={{
              backgroundColor: 'var(--theme-card-cyan)',
              borderBottom: '2px solid var(--theme-border)',
            }}
          >
            <h3
              className="text-sm font-bold uppercase"
              style={{
                color: 'var(--theme-text)',
              }}
            >
              {t('notifications.title')}
            </h3>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={handleMarkAllAsRead}
                className="text-xs font-bold uppercase px-2 py-1 transition-all"
                style={{
                  color: 'var(--theme-text)',
                  backgroundColor: 'var(--theme-card-pink)',
                  border: '2px solid var(--theme-border)',
                  boxShadow: '2px 2px 0px var(--theme-border)',
                }}
              >
                {t('notifications.markAllAsRead')}
              </button>
            )}
          </div>

          {/* Notifications List */}
          <div className="max-h-[60vh] overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2Icon className="h-6 w-6 animate-spin" style={{ color: 'var(--theme-accent)' }} />
              </div>
            ) : notifications.length === 0 ? (
              <div className="text-center py-8">
                <BellIcon
                  className="h-12 w-12 mx-auto mb-3 opacity-30"
                  style={{ color: 'var(--theme-text)' }}
                />
                <p className="text-sm" style={{ color: 'var(--theme-text-muted)' }}>
                  {t('notifications.noNotifications')}
                </p>
              </div>
            ) : (
              <div className="p-2">
                {notifications.map((notification) => {
                  const isAdmin = isAdminNotification(notification);

                  return (
                    <div
                      key={notification.id}
                      onClick={() => handleNotificationClick(notification)}
                      className="flex items-start gap-3 p-3 mb-2 cursor-pointer group transition-all hover:translate-x-1"
                      style={{
                        backgroundColor: notification.isRead ? 'transparent' : 'var(--theme-card-purple)',
                        border: '2px solid var(--theme-border)',
                        boxShadow: notification.isRead ? 'none' : '2px 2px 0px var(--theme-border)',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'var(--theme-card-purple)';
                        e.currentTarget.style.boxShadow = '2px 2px 0px var(--theme-border)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = notification.isRead ? 'transparent' : 'var(--theme-card-purple)';
                        e.currentTarget.style.boxShadow = notification.isRead ? 'none' : '2px 2px 0px var(--theme-border)';
                      }}
                    >
                      {/* Arcade Unread Indicator */}
                      {!notification.isRead && (
                        <div
                          className="w-3 h-3 mt-1.5 flex-shrink-0 animate-pulse"
                          style={{
                            backgroundColor: 'var(--theme-card-yellow)',
                            border: '2px solid var(--theme-border)',
                          }}
                        />
                      )}

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        {/* Admin Badge */}
                        {isAdmin && (
                          <div className="flex items-center gap-1 mb-1">
                            <ShieldCheckIcon className="h-3 w-3" style={{ color: '#FFB6F3' }} />
                            <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color: '#FFB6F3' }}>
                              Admin Notification
                            </span>
                          </div>
                        )}

                        <p className="text-sm font-semibold group-hover:underline" style={{ color: 'var(--theme-text)' }}>
                          {notification.title}
                        </p>
                        <p className="text-xs mt-1 line-clamp-2" style={{ color: 'var(--theme-text-muted)' }}>
                          {notification.message}
                        </p>
                        <p className="text-[10px] mt-1.5 flex items-center gap-2" style={{ color: 'var(--theme-text-muted)' }}>
                          <span>{formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}</span>
                          {notification.bookingId && (
                            <>
                              <span>•</span>
                              <span className="font-medium">Booking</span>
                            </>
                          )}
                        </p>
                      </div>

                      {/* Arcade Actions */}
                      <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                        {!notification.isRead && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              markAsRead.mutate(notification.id);
                            }}
                            className="p-1.5 transition-all"
                            style={{
                              color: 'var(--theme-text)',
                              backgroundColor: 'var(--theme-card-green)',
                              border: '2px solid var(--theme-border)',
                              boxShadow: '2px 2px 0px var(--theme-border)',
                            }}
                            aria-label={t('notifications.markAsRead')}
                            title={t('notifications.markAsRead')}
                          >
                            <CheckIcon className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={(e) => handleDelete(e, notification.id)}
                          className="p-1.5 transition-all"
                          style={{
                            color: 'var(--theme-text)',
                            backgroundColor: 'var(--theme-card-pink)',
                            border: '2px solid var(--theme-border)',
                            boxShadow: '2px 2px 0px var(--theme-border)',
                          }}
                          aria-label={t('notifications.delete')}
                          title={t('notifications.delete')}
                        >
                          <XIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div
              className="p-3 text-center"
              style={{
                backgroundColor: 'var(--theme-card-cyan)',
                borderTop: '2px solid var(--theme-border)',
              }}
            >
              <button
                type="button"
                onClick={() => {
                  navigate('/dashboard/notifications');
                  setIsOpen(false);
                }}
                className="text-xs font-bold uppercase px-4 py-2 transition-all"
                style={{
                  color: 'var(--theme-text)',
                  backgroundColor: 'var(--theme-card-purple)',
                  border: '2px solid var(--theme-border)',
                  boxShadow: '2px 2px 0px var(--theme-border)',
                }}
              >
                {t('notifications.viewAll')}
              </button>
            </div>
          )}
        </div>
        )}
      </div>

      {/* Bell Notification Bubble */}
      {showBubble && (
        <BellNotificationBubble
          message={bubbleMessage}
          onClose={() => setShowBubble(false)}
          duration={5000}
        />
      )}
    </>
  );
}
